'use client'

import type { MotionValue } from 'motion/react'
import type { ReactNode } from 'react'
import { useRef, useState } from 'react'
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from 'motion/react'

import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

export interface ScrollStoryItem {
  id: string
  media: ReactNode
  title: string
  text: string
  /** Blend the media into the panel (mix-blend-lighten), for a video whose background is a shade darker. */
  blendLighten?: boolean
}

interface ScrollStoryProps {
  items: readonly ScrollStoryItem[]
  className?: string
}

/** Scroll distance, in vh, that each item after the first holds the stage. */
const STEP_VH = 70

/**
 * Benefits told one at a time. From `md` up the stage pins while the page scrolls past it, and the
 * crossfade between items is tied to the scroll position rather than played on a timer, so it moves
 * exactly as fast as the reader scrolls. The rail on the left shows where you are and jumps to an item
 * on click. Every item stays in the DOM, so the copy is still indexed and the stage never changes
 * height. Phones get the items stacked, each fading up as it scrolls in; with reduced motion every
 * screen gets the stack without animation.
 */
export function ScrollStory({ items, className }: ScrollStoryProps) {
  const reduceMotion = useReducedMotion()

  return (
    <div className={cn('bg-inherit', className)}>
      {!reduceMotion && <PinnedStory items={items} className='hidden md:block' />}
      <StackedStory items={items} animate={!reduceMotion} className={cn(!reduceMotion && 'md:hidden')} />
    </div>
  )
}

/**
 * The scroll progress where item `index` fades in and out. Each item holds the stage around its own
 * point; over the middle 40% of the gap to its neighbour it fades out and the neighbour fades in,
 * meeting at zero halfway so two items never overlap. The first and last items stay shown at the ends.
 */
function fadeWindow(index: number, count: number) {
  const step = 1 / Math.max(count - 1, 1)
  const center = index * step
  return [center - 0.5 * step, center - 0.3 * step, center + 0.3 * step, center + 0.5 * step]
}

function PinnedStory({ items, className }: { items: readonly ScrollStoryItem[]; className?: string }) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)
  const last = Math.max(items.length - 1, 1)
  const { scrollYProgress } = useScroll({ target: trackRef, offset: ['start start', 'end end'] })

  // Only for aria-current and inert; the visuals read scrollYProgress directly
  useMotionValueEvent(scrollYProgress, 'change', (progress) => {
    setActive(Math.min(items.length - 1, Math.max(0, Math.round(progress * last))))
  })

  const goTo = (index: number) => {
    const track = trackRef.current
    if (!track) return
    const top = track.getBoundingClientRect().top + window.scrollY
    const distance = track.offsetHeight - window.innerHeight
    window.scrollTo({ top: top + (distance * index) / last, behavior: 'smooth' })
  }

  return (
    <div
      ref={trackRef}
      className={cn('relative bg-inherit', className)}
      style={{ height: `calc(100svh + ${(items.length - 1) * STEP_VH}vh)` }}
    >
      {/* pt-17 clears the sticky site nav. bg-inherit: sticky starts a stacking context, so the stage
          needs the panel colour for mix-blend-lighten to blend against */}
      <div className='sticky top-0 flex h-svh items-center bg-inherit pt-17'>
        <div className='flex w-full items-center gap-12'>
          <div className='flex min-w-0 flex-1 items-center gap-8'>
            <div className='flex shrink-0 flex-col items-center gap-1.5'>
              {items.map((item, index) => (
                <RailMark
                  key={item.id}
                  label={item.title}
                  index={index}
                  count={items.length}
                  progress={scrollYProgress}
                  current={index === active}
                  onClick={() => goTo(index)}
                />
              ))}
            </div>

            <div className='grid min-w-0 flex-1'>
              {items.map((item, index) => (
                <StoryText
                  key={item.id}
                  item={item}
                  index={index}
                  count={items.length}
                  progress={scrollYProgress}
                  current={index === active}
                />
              ))}
            </div>
          </div>

          <div className='grid min-w-0 flex-1'>
            {items.map((item, index) => (
              <StoryMedia
                key={item.id}
                item={item}
                index={index}
                count={items.length}
                progress={scrollYProgress}
                current={index === active}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

interface LayerProps {
  index: number
  count: number
  progress: MotionValue<number>
  current: boolean
}

function RailMark({
  label,
  index,
  count,
  progress,
  current,
  onClick,
}: LayerProps & { label: string; onClick: () => void }) {
  const step = 1 / Math.max(count - 1, 1)
  const range = [index * step - step, index * step, index * step + step]
  const height = useTransform(progress, range, [18, 44, 18])
  const opacity = useTransform(progress, range, [0.35, 1, 0.35])

  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={label}
      aria-current={current ? 'step' : undefined}
      className='flex w-8 cursor-pointer justify-center py-1'
    >
      <motion.span className='bg-neutral-white block w-1 rounded-full' style={{ height, opacity }} />
    </button>
  )
}

function StoryText({ item, index, count, progress, current }: LayerProps & { item: ScrollStoryItem }) {
  const fade = fadeWindow(index, count)
  const opacity = useTransform(progress, fade, [0, 1, 1, 0])
  const y = useTransform(progress, fade, [24, 0, 0, -24])

  return (
    <motion.div
      className='flex flex-col gap-3 [grid-area:1/1]'
      style={{ opacity, y }}
      aria-hidden={!current}
      inert={!current}
    >
      <h2 className={typographyVariants({ variant: 'headline-lg' })}>{item.title}</h2>
      <p className={typographyVariants({ variant: 'body-lg' })}>{item.text}</p>
    </motion.div>
  )
}

function StoryMedia({ item, index, count, progress, current }: LayerProps & { item: ScrollStoryItem }) {
  const fade = fadeWindow(index, count)
  const opacity = useTransform(progress, fade, [0, 1, 1, 0])
  const scale = useTransform(progress, fade, [0.96, 1, 1, 0.96])

  return (
    <motion.div
      className={cn('[grid-area:1/1]', item.blendLighten && 'mix-blend-lighten')}
      style={{ opacity, scale }}
      aria-hidden={!current}
      inert={!current}
    >
      {item.media}
    </motion.div>
  )
}

function StackedStory({
  items,
  animate,
  className,
}: {
  items: readonly ScrollStoryItem[]
  animate: boolean
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-18', className)}>
      {items.map((item, index) => (
        <motion.div
          key={item.id}
          className={cn('flex flex-col items-center gap-12 md:flex-row', index % 2 === 1 && 'md:flex-row-reverse')}
          initial={animate ? { opacity: 0, y: 32 } : false}
          whileInView={animate ? { opacity: 1, y: 0 } : undefined}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className={cn('w-full min-w-0 md:flex-1', item.blendLighten && 'mix-blend-lighten')}>{item.media}</div>
          <div className='flex w-full flex-col gap-3 md:flex-1'>
            <h2 className={typographyVariants({ variant: 'headline-lg' })}>{item.title}</h2>
            <p className={typographyVariants({ variant: 'body-lg' })}>{item.text}</p>
          </div>
        </motion.div>
      ))}
    </div>
  )
}
